import {
  getHcpSignupEnvValidationConfig,
  getHcpSignupEnvLicenseFieldConfig,
  getHcpSignupEnvEducationConfig,
  getHcpSignupEnvExperienceConfig,
} from '../../config/hcpSignupEnvConfig';
import { getValidatedFileConfig } from '../../utils/formCrafter/fileTypeUtils';
import { DEFAULT_FILE_CONFIG } from '../../constants/formCrafter.constant';

/** Merged at build time from `REACT_APP_HCP_SIGNUP_*` (see `.env.example`). */
export const validationConfigDefaults = {
  ...getHcpSignupEnvValidationConfig(),
};

/** Per-step-2 license UI / validation (merged with draft snapshot on server). */
export const licenseFieldConfigDefaults = {
  ...getHcpSignupEnvLicenseFieldConfig(),
};

/** Step 3 education + experience (Yup + optional UI flags). */
export const educationExperienceConfigDefaults = {
  education: getHcpSignupEnvEducationConfig(),
  experience: getHcpSignupEnvExperienceConfig(),
};

/**
 * Apollo TextField / Select / DatePicker (via TextFieldProps) in HCP signup steps (boxed controls).
 */
export const hcpSignupFormFieldVariant = 'standard';

/**
 * Pass to Apollo `DatePicker` as `TextFieldProps={hcpSignupDatePickerTextFieldProps}` so the inner
 * text field uses the same outline style as signup (DatePicker `variant` alone is not enough).
 */
export const hcpSignupDatePickerTextFieldProps = Object.freeze({
  variant: hcpSignupFormFieldVariant,
  margin: 'none',
});

/**
 * Apollo TextField props aligned with signup + {@link wizardFormGrid} (vertical rhythm from FormControl margins).
 */
export const hcpSignupTextFieldProps = Object.freeze({
  variant: hcpSignupFormFieldVariant,
  margin: 'none',
  fullWidth: true,
});

/**
 * Customer profile maintenance (tabs under `/enduser/profile`): match {@link ../../common/Form/textInput}
 * and super-admin forms (Apollo default `standard` underline).
 */
export const hcpProfileFormFieldVariant = 'standard';

export const hcpProfileDatePickerTextFieldProps = Object.freeze({
  variant: hcpProfileFormFieldVariant,
  margin: 'none',
});

export const hcpProfileTextFieldProps = Object.freeze({
  variant: hcpProfileFormFieldVariant,
  margin: 'none',
  fullWidth: true,
});

/** Map common upload extensions to MIME types for `<input type="file" accept>`. */
const HCP_UPLOAD_EXT_TO_MIME = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
};

function normalizeHcpAcceptExt(x) {
  return String(x).trim().toLowerCase().replace(/^\./, '');
}

/**
 * Comma-separated `.<ext>` list for `getValidatedAcceptString` (whitelist against EXTENSION_TO_MIME).
 */
export function hcpSignupAcceptExtToDotAccept(acceptExt) {
  const list =
    Array.isArray(acceptExt) && acceptExt.length
      ? acceptExt.map(normalizeHcpAcceptExt).filter(Boolean)
      : ['pdf', 'jpg', 'jpeg', 'png'];
  const seen = new Set();
  const parts = [];
  for (const e of list) {
    if (seen.has(e)) continue;
    seen.add(e);
    parts.push(`.${e}`);
  }
  return parts.join(',');
}

/**
 * `accept` attribute for file inputs: MIME tokens plus dot extensions (Safari / macOS show PDF+images reliably).
 */
export function hcpSignupAcceptExtToHtmlAccept(acceptExt) {
  const list =
    Array.isArray(acceptExt) && acceptExt.length
      ? acceptExt.map(normalizeHcpAcceptExt).filter(Boolean)
      : ['pdf', 'jpg', 'jpeg', 'png'];
  const mimes = new Set();
  const dots = new Set();
  let wantsJpeg = false;
  for (const e of list) {
    dots.add(`.${e}`);
    const mime = HCP_UPLOAD_EXT_TO_MIME[e];
    if (mime === 'image/jpeg') wantsJpeg = true;
    else if (mime) mimes.add(mime);
  }
  if (wantsJpeg) mimes.add('image/jpeg');
  return [...mimes, ...dots].join(',');
}

/** MIME types allowed for an extension list (used by client file validation). */
export function hcpSignupAcceptExtAllowedMimeTypes(acceptExt) {
  const list =
    Array.isArray(acceptExt) && acceptExt.length
      ? acceptExt.map(normalizeHcpAcceptExt).filter(Boolean)
      : ['pdf', 'jpg', 'jpeg', 'png'];
  const mimes = new Set();
  let wantsJpeg = false;
  for (const e of list) {
    const mime = HCP_UPLOAD_EXT_TO_MIME[e];
    if (mime === 'image/jpeg') wantsJpeg = true;
    else if (mime) mimes.add(mime);
  }
  if (wantsJpeg) mimes.add('image/jpeg');
  return mimes;
}

/** Helper line under file uploads: types and max size from env-driven lists. */
export function formatHcpFileUploadHint(acceptExt, maxMb, required) {
  const list =
    Array.isArray(acceptExt) && acceptExt.length
      ? acceptExt.map(normalizeHcpAcceptExt).filter(Boolean)
      : ['pdf', 'jpg', 'jpeg', 'png'];
  const uniq = [...new Set(list)].map((e) => e.toUpperCase());
  const types =
    uniq.length === 1
      ? uniq[0]
      : uniq.length === 2
        ? `${uniq[0]} or ${uniq[1]}`
        : `${uniq.slice(0, -1).join(', ')}, or ${uniq[uniq.length - 1]}`;
  const mb = typeof maxMb === 'number' && maxMb > 0 ? maxMb : 10;
  return `${types} — max ${mb} MB. ${required ? 'Required.' : 'Optional.'}`;
}

/** @deprecated Use {@link hcpSignupAcceptExtToDotAccept} */
export const idImageAcceptExtToDotAccept = hcpSignupAcceptExtToDotAccept;
/** @deprecated Use {@link hcpSignupAcceptExtToHtmlAccept} */
export const idImageAcceptExtToHtmlAccept = hcpSignupAcceptExtToHtmlAccept;
/** @deprecated Use {@link formatHcpFileUploadHint} */
export const formatIdImageUploadHint = formatHcpFileUploadHint;

/**
 * Apollo `FileUpload` fileConfig: same accept + max size rules as ID document fields.
 */
function buildHcpSignupFileConfigFromIdImageFields(maxItems, acceptExt, maxMb) {
  const dotAccept = hcpSignupAcceptExtToDotAccept(acceptExt);
  const htmlAccept = hcpSignupAcceptExtToHtmlAccept(acceptExt);
  const base = getValidatedFileConfig(
    {
      type: 'file',
      accept: dotAccept,
      maxItems,
      maxSize: typeof maxMb === 'number' && maxMb > 0 ? maxMb : undefined,
    },
    DEFAULT_FILE_CONFIG.file
  );
  return { ...base, accept: htmlAccept };
}

/** Step 1 ID document fileConfig (see FileFieldInput).
 * @param {object} [validationConfig] merged with {@link validationConfigDefaults}
 */
export function buildHcpSignupIdDocumentFileConfig(validationConfig = {}) {
  const c = { ...validationConfigDefaults, ...validationConfig };
  return buildHcpSignupFileConfigFromIdImageFields(
    1,
    c.idImageAcceptExt,
    c.idImageMaxMb
  );
}

/**
 * Client validation and Apollo FileUpload `fileConfig` for every HCP sign-up
 * attachment after Step 1 (license scans, accreditation, internship certificate,
 * optional relevant files). Matches Step 1 ID document: `REACT_APP_HCP_SIGNUP_ID_IMAGE_*`.
 */
export function getHcpSignupWizardAttachmentFileValidationOpts() {
  return {
    maxMb: validationConfigDefaults.idImageMaxMb,
    acceptExt: validationConfigDefaults.idImageAcceptExt,
  };
}

/**
 * @param {number} maxItems
 * @returns {object}
 */
export function buildHcpSignupWizardAttachmentFileConfig(maxItems) {
  const { acceptExt, maxMb } = getHcpSignupWizardAttachmentFileValidationOpts();
  return buildHcpSignupFileConfigFromIdImageFields(maxItems, acceptExt, maxMb);
}

export const nationalityOptions = [
  { label: 'Egypt', value: 'EG' },
  { label: 'Saudi Arabia', value: 'SA' },
  { label: 'United Arab Emirates', value: 'AE' },
  { label: 'Other', value: 'OTHER' },
];

export const addressCountryOptions = [
  { label: 'Egypt', value: 'EG' },
  { label: 'Saudi Arabia', value: 'SA' },
  { label: 'United Arab Emirates', value: 'AE' },
  { label: 'Other', value: 'OTHER' },
];

const ADDRESS_BY_COUNTRY = {
  EG: {
    states: [
      { label: 'Cairo', value: 'Cairo' },
      { label: 'Alexandria', value: 'Alexandria' },
      { label: 'Other', value: 'OTHER' },
    ],
    citiesByState: {
      Cairo: [
        { label: 'Cairo', value: 'Cairo' },
        { label: 'Giza', value: 'Giza' },
        { label: 'Other', value: 'OTHER' },
      ],
      Alexandria: [
        { label: 'Alexandria', value: 'Alexandria' },
        { label: 'Other', value: 'OTHER' },
      ],
      OTHER: [{ label: 'Other', value: 'OTHER' }],
    },
  },
  SA: {
    states: [
      { label: 'Riyadh', value: 'Riyadh' },
      { label: 'Makkah', value: 'Makkah' },
      { label: 'Eastern Province', value: 'Eastern Province' },
      { label: 'Other', value: 'OTHER' },
    ],
    citiesByState: {
      Riyadh: [
        { label: 'Riyadh', value: 'Riyadh' },
        { label: 'Other', value: 'OTHER' },
      ],
      Makkah: [
        { label: 'Jeddah', value: 'Jeddah' },
        { label: 'Makkah', value: 'Makkah' },
        { label: 'Other', value: 'OTHER' },
      ],
      'Eastern Province': [
        { label: 'Dammam', value: 'Dammam' },
        { label: 'Other', value: 'OTHER' },
      ],
      OTHER: [{ label: 'Other', value: 'OTHER' }],
    },
  },
  AE: {
    states: [
      { label: 'Dubai', value: 'Dubai' },
      { label: 'Abu Dhabi', value: 'Abu Dhabi' },
      { label: 'Sharjah', value: 'Sharjah' },
      { label: 'Ajman', value: 'Ajman' },
      { label: 'Other', value: 'OTHER' },
    ],
    citiesByState: {
      Dubai: [
        { label: 'Dubai', value: 'Dubai' },
        { label: 'Other', value: 'OTHER' },
      ],
      'Abu Dhabi': [
        { label: 'Abu Dhabi', value: 'Abu Dhabi' },
        { label: 'Al Ain', value: 'Al Ain' },
        { label: 'Other', value: 'OTHER' },
      ],
      Sharjah: [
        { label: 'Sharjah', value: 'Sharjah' },
        { label: 'Other', value: 'OTHER' },
      ],
      Ajman: [
        { label: 'Ajman', value: 'Ajman' },
        { label: 'Other', value: 'OTHER' },
      ],
      OTHER: [{ label: 'Other', value: 'OTHER' }],
    },
  },
  OTHER: {
    states: [{ label: 'Other', value: 'OTHER' }],
    citiesByState: {
      OTHER: [{ label: 'Other', value: 'OTHER' }],
    },
  },
};

/** @param {string} [countryCode] */
export function getAddressStateOptions(countryCode) {
  const key =
    countryCode && ADDRESS_BY_COUNTRY[countryCode] ? countryCode : 'OTHER';
  return ADDRESS_BY_COUNTRY[key].states;
}

/**
 * @param {string} [countryCode]
 * @param {string} [stateValue]
 */
export function getAddressCityOptions(countryCode, stateValue) {
  const key =
    countryCode && ADDRESS_BY_COUNTRY[countryCode] ? countryCode : 'OTHER';
  const byState = ADDRESS_BY_COUNTRY[key].citiesByState;
  if (stateValue && byState[stateValue]) return byState[stateValue];
  const firstState = ADDRESS_BY_COUNTRY[key].states[0]?.value;
  if (firstState && byState[firstState]) return byState[firstState];
  return byState.OTHER || [{ label: 'Other', value: 'OTHER' }];
}

export const countryCodeDialOptions = [
  { label: 'Egypt (+20)', value: '+20' },
  { label: 'Saudi Arabia (+966)', value: '+966' },
  { label: 'United Arab Emirates (+971)', value: '+971' },
];

/** Map API / legacy label text to {@link addressCountryOptions} codes (EG, SA, …). */
export function normalizeAddressCountryValue(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  if (addressCountryOptions.some((o) => o.value === s)) return s;
  const byLabel = addressCountryOptions.find(
    (o) => o.label.toLowerCase() === s.toLowerCase()
  );
  return byLabel ? byLabel.value : s;
}

/** Match stored dial code to {@link countryCodeDialOptions} (+20, …). */
export function normalizeDialCountryCode(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  if (countryCodeDialOptions.some((o) => o.value === s)) return s;
  const digits = s.replace(/\D/g, '');
  if (!digits) return s;
  const withPlus = `+${digits}`;
  if (countryCodeDialOptions.some((o) => o.value === withPlus)) return withPlus;
  return s;
}

/** If `value` is non-empty and missing from `options`, prepend a legacy option so selects stay controlled. */
export function mergeOptionIfMissing(options, value) {
  const v = String(value ?? '').trim();
  if (!v) return options;
  if (options.some((o) => o.value === v)) return options;
  return [{ label: v, value: v }, ...options];
}

/** Human-readable country label for profile/signup display. */
export function labelForAddressCountry(code) {
  const c = normalizeAddressCountryValue(code);
  if (!c) return '—';
  return addressCountryOptions.find((o) => o.value === c)?.label ?? c;
}

/** Human-readable state/resolved row label for address display. */
export function labelForAddressState(countryCode, stateValue) {
  const v = String(stateValue ?? '').trim();
  if (!v) return '—';
  const opts = getAddressStateOptions(countryCode);
  return opts.find((o) => o.value === v)?.label ?? v;
}

/** Human-readable city label for address display. */
export function labelForAddressCity(countryCode, stateValue, cityValue) {
  const v = String(cityValue ?? '').trim();
  if (!v) return '—';
  const opts = getAddressCityOptions(countryCode, stateValue);
  return opts.find((o) => o.value === v)?.label ?? v;
}

export const genderOptions = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
];

/** Internship / training (Step 3). */
export const yesNoDropdownOptions = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

export const accreditationStatusOptions = [
  { label: 'Accredited', value: 'accredited' },
  { label: 'Not accredited', value: 'not_accredited' },
  { label: 'Pending accreditation', value: 'pending' },
];
